/* eslint-disable prettier/prettier */
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { axiosInstance } from '../Services'

const baseUrl = process.env.REACT_APP_GATEWAY_URL || ''

export interface SelfServedClientUser {
  id: string
  userId: string | null
  selfServedClientId: string
  role: string
  isActive: boolean
  createdAt: string
  lastUpdated: string
  createdBy: string
  user: {
    id: string | null
    email: string
    firstName: string
    lastName: string
    status: string
  }
}

export interface SelfServedClient {
  id: string
  name: string
  description: string
  isActive: boolean
  creationTime: string
  lastUpdated: string
  createdBy: string
  users: SelfServedClientUser[]
}

export interface CreateClientRequest {
  name: string
  description: string
}

export interface UpdateClientRequest {
  id: string
  name: string
  description: string
  isActive: boolean
}

export interface AddUserToClientRequest {
  userEmail: string
  selfServedClientId: string
  role: string
  password: string
}

export interface RemoveUserFromClientRequest {
  userId: string | null
  selfServedClientId: string
}

// Get all self-served clients
export const useSelfServedClients = () => {
  return useQuery<SelfServedClient[]>(['self-served-clients'], async () => {
    const response = await axiosInstance.get(`${baseUrl}/admin/self-served/clients`, {
      headers: {
        Accept: 'application/json',
      },
    })
    return response.data
  })
}

// Create a new self-served client
export const useCreateSelfServedClient = () => {
  const queryClient = useQueryClient()

  return useMutation(
    async (data: CreateClientRequest) => {
      const response = await axiosInstance.post(`${baseUrl}/admin/self-served/clients`, data, {
        headers: {
          Accept: 'application/json',
        },
      })
      return response.data
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['self-served-clients'])
      },
    }
  )
}

// Update a self-served client
export const useUpdateSelfServedClient = () => {
  const queryClient = useQueryClient()

  return useMutation(
    async (data: UpdateClientRequest) => {
      const response = await axiosInstance.put(`${baseUrl}/admin/self-served/clients`, data, {
        headers: {
          Accept: 'application/json',
        },
      })
      return response.data
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['self-served-clients'])
      },
    }
  )
}

// Add user to self-served client
export const useAddUserToClient = () => {
  const queryClient = useQueryClient()

  return useMutation(
    async (data: AddUserToClientRequest) => {
      const response = await axiosInstance.post(`${baseUrl}/admin/self-served/clients/users`, data, {
        headers: {
          Accept: 'application/json',
        },
      })
      return response.data
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['self-served-clients'])
      },
    }
  )
}

// Remove user from self-served client
export const useRemoveUserFromClient = () => {
  const queryClient = useQueryClient()

  return useMutation(
    async (data: RemoveUserFromClientRequest) => {
      const response = await axiosInstance.delete(`${baseUrl}/admin/self-served/clients/users`, {
        data,
        headers: {
          Accept: 'application/json',
        },
      })
      return response.data
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['self-served-clients'])
      },
    }
  )
}
